import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LegalLayout from "@/components/LegalLayout";
import useDocumentMeta from "@/lib/useDocumentMeta";

/**
 * MarkdownPage — fetches a static markdown file from /public/legal/ and
 * renders it inside the public LegalLayout. Handles SEO meta + Tailwind
 * `prose` typography.
 *
 * Props:
 *   - sourcePath: path under /public, e.g. "/legal/terms-and-conditions.md"
 *   - title: <title> + og:title
 *   - description: <meta name="description">
 *
 * Why fetch instead of import? CRA's default webpack treats .md as a URL
 * asset, not a raw string. Moving the files to /public + fetching avoids any
 * loader config and survives CRA → Vite migration painlessly.
 */
const SEED = "data-markdown-source";

/* The markdown source a previous render baked into this page, if any.

   ⚠ This is what makes the pre-rendered page hydrate. The text arrives by
   fetch, so a first render that starts from `null` is always "Loading…" —
   while the pre-rendered HTML already holds the finished <article>. React
   cannot reconcile the two, throws #418 and swaps the article for the
   placeholder until the fetch lands: measured on /about, the page body
   blinked to "Loading…" on every visit. Reading the text back out of the
   page makes the first render identical to the HTML it is hydrating, and
   saves the fetch as well.

   Matched by attribute value, not a CSS selector, so no path needs escaping. */
function readSeed(sourcePath) {
  if (typeof document === "undefined") return null;
  for (const el of document.querySelectorAll(`script[${SEED}]`)) {
    if (el.getAttribute(SEED) !== sourcePath) continue;
    try { return JSON.parse(el.textContent); } catch { return null; }
  }
  return null;
}

export default function MarkdownPage({ sourcePath, title, description }) {
  useDocumentMeta(title, description);
  const [content, setContent] = useState(() => readSeed(sourcePath));
  const [error, setError] = useState(false);

  useEffect(() => {
    if (content !== null) return;   // seeded from the page — nothing to fetch
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(sourcePath, { cache: "default" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (!cancelled) setContent(text);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [sourcePath, content]);

  return (
    <LegalLayout>
      {content === null && !error && (
        <div className="text-charcoal-muted text-sm" data-testid="markdown-loading">Loading…</div>
      )}
      {error && (
        <div className="text-terracotta text-sm" data-testid="markdown-error">
          Could not load this page. Please try refreshing.
        </div>
      )}
      {content && (
        <article
          className="prose prose-stone prose-headings:font-heading prose-headings:text-charcoal prose-h1:text-4xl prose-h1:font-semibold prose-h1:mb-4 prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-h3:text-lg prose-p:text-charcoal/85 prose-li:text-charcoal/85 prose-strong:text-charcoal prose-a:text-moss hover:prose-a:text-moss-dark prose-a:no-underline hover:prose-a:underline max-w-none"
          data-testid="markdown-content"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </article>
      )}
      {/* The source text, for the next load's first render — see readSeed.
          A non-JS type never executes, and escaping every "<" means no
          "</script>" in the markdown can end the tag early. */}
      {content && (
        <script
          type="application/json"
          {...{ [SEED]: sourcePath }}
          dangerouslySetInnerHTML={{ __html: JSON.stringify(content).replace(/</g, "\\u003c") }}
        />
      )}
    </LegalLayout>
  );
}
